/**
 * Module paramétrage : sous-navigation vers les écrans d'administration
 * (`21-workflows-et-diagrammes.md`). Réservé à `admin-totalenergies` par la route elle-même — voir
 * `App.tsx`, qui ne monte ce module que pour ce profil.
 *
 * La page d'accueil présente chaque rubrique comme une carte titrée et décrite plutôt qu'une simple
 * barre de liens : les notions du module (référentiels, seuils, règles d'alerte…) ne sont pas
 * spontanément compréhensibles pour un administrateur métier sans connaissance technique du système.
 */
import { Suspense, lazy } from "react";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";

import { Chargement } from "../../shared/composants/Etats";
import { EcranAudit } from "./EcranAudit";
import { EcranModelesExport } from "./EcranModelesExport";
import { EcranReferentiels } from "./EcranReferentiels";
import { EcranReglesAlerte } from "./EcranReglesAlerte";
import { EcranSeuils } from "./EcranSeuils";
import { EcranUtilisateurs } from "./EcranUtilisateurs";

// `maplibre-gl` pèse à lui seul plus que le reste de l'application (`App.tsx`, même raison pour
// `EcranCarte`) : le charger paresseusement évite d'imposer son poids à un administrateur qui n'ouvre
// jamais l'éditeur de géozones.
const EcranGeozones = lazy(() =>
  import("./EcranGeozones").then((module) => ({ default: module.EcranGeozones })),
);

const RUBRIQUES = [
  {
    chemin: "geozones",
    titre: "Géozones",
    description:
      "Créer et modifier les zones géographiques utilisées pour localiser automatiquement les GRV.",
  },
  {
    chemin: "referentiels",
    titre: "Référentiels",
    description: "Gérer les listes de valeurs métier utilisées dans l'application.",
  },
  {
    chemin: "seuils",
    titre: "Seuils",
    description: "Définir les valeurs déclenchant certains indicateurs ou anomalies.",
  },
  {
    chemin: "alertes-regles",
    titre: "Règles d'alerte",
    description: "Définir les conditions, destinataires, relances et escalades des alertes.",
  },
  {
    chemin: "exports-modeles",
    titre: "Modèles d'export",
    description: "Définir le contenu et la présentation des exports standards.",
  },
  {
    chemin: "utilisateurs",
    titre: "Utilisateurs et droits",
    description: "Créer, modifier, désactiver les comptes et gérer leurs profils.",
  },
  {
    chemin: "audit",
    titre: "Journal d'audit",
    description: "Consulter l'historique des principales actions et modifications.",
  },
];

function EcranAccueilParametrage() {
  return (
    <div className="grille-cartes">
      {RUBRIQUES.map((rubrique) => (
        <NavLink key={rubrique.chemin} to={rubrique.chemin} className="carte carte--lien">
          <h2>{rubrique.titre}</h2>
          <p className="carte__description">{rubrique.description}</p>
        </NavLink>
      ))}
    </div>
  );
}

export function EcranParametrage() {
  const emplacement = useLocation();
  const surAccueil = emplacement.pathname === "/parametrage" || emplacement.pathname === "/parametrage/";

  return (
    <div className="parametrage">
      <h1>Paramétrage</h1>

      {!surAccueil && (
        <nav className="parametrage__onglets" aria-label="Sections du paramétrage">
          <NavLink to="/parametrage">Accueil</NavLink>
          {RUBRIQUES.map((rubrique) => (
            <NavLink key={rubrique.chemin} to={`/parametrage/${rubrique.chemin}`}>
              {rubrique.titre}
            </NavLink>
          ))}
        </nav>
      )}

      <Routes>
        <Route path="/" element={<EcranAccueilParametrage />} />
        <Route
          path="geozones"
          element={
            <Suspense fallback={<Chargement libelle="Chargement des géozones…" />}>
              <EcranGeozones />
            </Suspense>
          }
        />
        <Route path="referentiels" element={<EcranReferentiels />} />
        <Route path="seuils" element={<EcranSeuils />} />
        <Route path="alertes-regles" element={<EcranReglesAlerte />} />
        <Route path="exports-modeles" element={<EcranModelesExport />} />
        <Route path="utilisateurs" element={<EcranUtilisateurs />} />
        <Route path="audit" element={<EcranAudit />} />
      </Routes>
    </div>
  );
}
